import React, { type SyntheticEvent, useCallback, useMemo } from 'react';
import {
	VIEW_KIND_BOARD,
	VIEW_KIND_TABLE,
	VIEW_KIND_MATRIX,
	VIEW_KIND_TIMELINE,
	VIEW_KIND_SECTION,
	VIEW_KIND_TREE,
} from '@jira/polaris__domain-view/src/viewKindTimeline.constants.tsx';
import type { ViewKind } from '@jira/polaris__domain-view/src/ViewKind.types.tsx';
import { useNavigationActions } from '@jira/polaris__component-navigation-store/src/useNavigationActions.tsx';
import {
	useContainerViewsCount,
	useHasReachedViewsLimit,
} from '@jira/polaris__component-navigation-store/src/useIsNavigationLocked.tsx';
import { ViewLimitBanner } from '@jira/polaris__component-limits/src/ViewLimitBanner.tsx';
import { ViewsetLimitBanner } from '@jira/polaris__component-limits/src/ViewsetLimitBanner.tsx';
import Section from '@atlaskit/menu/section';
import {
	useAnalyticsL3,
	useContainerId,
	useSectionsEnabled,
	useViewsEntityLimitType,
	useViewsetsEntityLimitType,
} from '../../../controllers/context.tsx';
import type { NavigationContainer } from '../../../common/types.tsx';
import { BasicViewSets } from './BasicViewSets.tsx';
import { BasicViewsList } from './BasicViewsList.tsx';

type ViewMenuItemsProps = {
	navigationContainer: NavigationContainer;
	container?: string;
	onClick?: (arg: { type: ViewKind; container?: string }) => void;
};

export const ViewMenuItems = ({
	container,
	navigationContainer,
	onClick,
}: ViewMenuItemsProps): React.JSX.Element => {
	const { sendCreateViewDropDownOnProjectMenuItemAnalyticsEvent } = useAnalyticsL3();
	const { createView, insertViewSet } = useNavigationActions();
	const sectionsEnabled = useSectionsEnabled();
	const viewsEntityLimitType = useViewsEntityLimitType();
	const viewsetsEntityLimitType = useViewsetsEntityLimitType();
	const containerId = useContainerId();
	const viewCount = useContainerViewsCount({ containerId });
	const hasReachedViewsLimit = useHasReachedViewsLimit({
		containerId,
		entityLimitType: viewsEntityLimitType,
	});
	const hasReachedViewsetsLimit = useHasReachedViewsLimit({
		containerId,
		entityLimitType: viewsetsEntityLimitType,
	});

	const trackMenuItemAnalyticsEvent = useCallback(
		(type: ViewKind) => {
			sendCreateViewDropDownOnProjectMenuItemAnalyticsEvent({
				itemId: type,
				viewParams: {
					viewKind: type,
				},
				container: navigationContainer,
			});
		},
		[navigationContainer, sendCreateViewDropDownOnProjectMenuItemAnalyticsEvent],
	);

	const createViewHandler = useCallback(
		(type: ViewKind) => (event: SyntheticEvent) => {
			event.preventDefault();

			// Notify the parent component
			onClick?.({ type });

			// Track the event
			trackMenuItemAnalyticsEvent(type);

			if (sectionsEnabled && type === 'SECTION') {
				insertViewSet();
				return;
			}
			createView(type, container);
		},
		[onClick, trackMenuItemAnalyticsEvent, sectionsEnabled, createView, container, insertViewSet],
	);

	const createViewHandlers = useMemo(() => {
		const handlers = {
			[VIEW_KIND_TABLE]: createViewHandler(VIEW_KIND_TABLE),
			[VIEW_KIND_BOARD]: createViewHandler(VIEW_KIND_BOARD),
			[VIEW_KIND_TIMELINE]: createViewHandler(VIEW_KIND_TIMELINE),
			[VIEW_KIND_MATRIX]: createViewHandler(VIEW_KIND_MATRIX),
			[VIEW_KIND_SECTION]: createViewHandler(VIEW_KIND_SECTION),
			[VIEW_KIND_TREE]: createViewHandler(VIEW_KIND_TREE),
		} as const;

		return handlers;
	}, [createViewHandler]);

	return (
		<>
			{hasReachedViewsLimit && <ViewLimitBanner viewCount={viewCount} />}
			{hasReachedViewsetsLimit && <ViewsetLimitBanner viewCount={viewCount} />}
			<BasicViewsList
				createViewHandlers={createViewHandlers}
				hasReachedViewsLimit={hasReachedViewsLimit}
			/>

			{!container && sectionsEnabled && viewsetsEntityLimitType && (
				<Section isList hasSeparator>
					<BasicViewSets createViewHandlers={createViewHandlers} />
				</Section>
			)}
		</>
	);
};
